/**
 * Composer 등록형 행 위젯 `improvement-image`의 web 호스트 어댑터 — rc는 API를 모르고 생성 실행만
 * 호스트에 위임한다(결정 2026-09-14-risk-assessment-report-composed-transition §4,
 * `composerSuggestions`의 검색 제안 어댑터와 같은 축).
 *
 * 구 RA 작성 페이지(new-risk-assessment-report.tsx regenerateImage) 이식:
 *   - 생성: POST /ai/generate-improvement-image { photoUrl, findings[], organizationId,
 *           riskSummary, improvementPlan }
 * 유사 검색(`POST /ai/improvement-images/search`)은 2026-09-14 사용자 결정으로 부르지 않는다 — 텍스트
 * 임베딩 유사도가 이미지 적합성을 보장하지 않아 항상 새로 생성. 이미지 임베딩 도입 시 재추가(서버 API는
 * 그대로 있다).
 * ⚠️ 컨트롤러 DTO(apps/server/src/ai/ai-image.controller.ts)의 조직 키는 `organizationId`다 —
 * 구 페이지는 `companyId`로 보내고 있었다(코드를 따른다).
 */
import type {
  ComposerImprovementImageHost,
  ImprovementImageFinding,
  ImprovementImageGenerated,
} from '@safetyhub/report-composer';
import { api } from './api';

/**
 * 생성 API 본문 — 서버 FindingInput 형상({description, location?, measures?})으로 행 입력을 접는다.
 * riskSummary/improvementPlan 최상위도 함께 보낸다(서버가 findings가 비었을 때 폴백으로 읽는다).
 */
export function improvementImageGenerateBody(
  organizationId: string,
  finding: ImprovementImageFinding,
  model?: string,
): Record<string, unknown> {
  const description = finding.riskSummary || finding.improvementPlan || '';
  const one: Record<string, unknown> = { description };
  if (finding.locationText) one.location = finding.locationText;
  if (finding.improvementPlan) one.measures = [finding.improvementPlan];
  if (finding.riskTypes?.length) one.category = finding.riskTypes.join(', ');
  const body: Record<string, unknown> = {
    photoUrl: finding.photoUrl ?? '',
    findings: [one],
    organizationId,
    riskSummary: finding.riskSummary ?? '',
    improvementPlan: finding.improvementPlan ?? '',
  };
  if (model) body.model = model;
  return body;
}

/**
 * 동시 생성 상한 기본값 — 작성 진입 시 rc 위젯이 행마다 자동 생성을 띄우므로(병렬, 다중 점검 합본은
 * 점검 수 × 발견사항 수) 호스트가 상한을 건다(서버 `ai-image.controller`엔 Throttle이 없다).
 */
export const IMPROVEMENT_IMAGE_CONCURRENCY = 3;

export interface ComposerImprovementImageHostOptions {
  /** 동시 생성 상한(기본 IMPROVEMENT_IMAGE_CONCURRENCY). 넘치면 슬롯이 빌 때까지 대기. */
  concurrency?: number;
  /**
   * 호출 시점의 취소 신호 — 호스트를 쓰는 화면이 떠나면 대기·진행 중 요청을 모두 끊는다.
   * 함수인 이유: 훅이 StrictMode 재마운트마다 AbortController를 새로 만들므로 호스트는 신호를
   * 닫아 넣지 않고 매 호출에 읽는다.
   */
  getSignal?: () => AbortSignal | undefined;
}

/** 취소된 요청이 던지는 오류 — rc 위젯은 isAlive 게이트로 버리고, 살아 있으면 실패 문구를 낸다. */
export class ImprovementImageAbortedError extends Error {
  constructor() {
    super('개선 이미지 생성이 취소되었습니다.');
    this.name = 'ImprovementImageAbortedError';
  }
}

/** 카운팅 세마포어 — FIFO 대기. */
function createSemaphore(limit: number) {
  let active = 0;
  const waiters: Array<() => void> = [];
  return {
    async acquire(): Promise<void> {
      if (active < limit) {
        active += 1;
        return;
      }
      await new Promise<void>((resolve) => waiters.push(resolve));
      active += 1;
    },
    release(): void {
      active -= 1;
      const next = waiters.shift();
      if (next) next();
    },
  };
}

/**
 * 호스트 구현(rc 계약) — organizationId를 닫아 넣는다. 실패는 그대로 reject(rc가 실패 문구 표시).
 * 동시 호출은 세마포어로 상한을 걸고, `getSignal()`이 준 신호가 끊기면 대기 중인 호출은 호출 없이
 * reject, 진행 중인 호출은 axios가 취소한다.
 */
export function createComposerImprovementImageHost(
  organizationId: string,
  options: ComposerImprovementImageHostOptions = {},
): ComposerImprovementImageHost {
  const sem = createSemaphore(options.concurrency ?? IMPROVEMENT_IMAGE_CONCURRENCY);
  const getSignal = options.getSignal ?? (() => undefined);
  return {
    async generate(finding, opts) {
      const signal = getSignal();
      if (signal?.aborted) throw new ImprovementImageAbortedError();
      await sem.acquire();
      try {
        // 대기하는 사이 화면이 떠났으면 호출하지 않는다(비용 소진 방지).
        if (signal?.aborted) throw new ImprovementImageAbortedError();
        const res = await api.post(
          '/ai/generate-improvement-image',
          improvementImageGenerateBody(organizationId, finding, opts?.model),
          { signal },
        );
        const data = res.data?.data as
          | { imageUrl?: unknown; prompt?: unknown; model?: unknown; fileId?: unknown }
          | undefined;
        const url = typeof data?.imageUrl === 'string' ? data.imageUrl : '';
        if (!url) throw new Error('개선 이미지 생성 응답에 imageUrl이 없습니다.');
        const out: ImprovementImageGenerated = { url };
        if (typeof data?.prompt === 'string') out.prompt = data.prompt;
        if (typeof data?.model === 'string') out.model = data.model;
        // P3-8 원장 포인터 — 이 호스트는 **옮기기만** 한다. 여기서 빠지면 값 객체에 실리지 않고,
        // 결재 완료 훅이 `improvement_images.file_id`를 채울 재료를 영영 못 받아 생성물이
        // 「미채택 고아」로 남는다(사슬이 조용히 끊기는 자리).
        if (typeof data?.fileId === 'string') out.fileId = data.fileId;
        return out;
      } finally {
        sem.release();
      }
    },
  };
}
